/**
 * Editor i18n (spec #304): `<I18nProvider locale>` wraps the editor screens,
 * `useI18n()` hands out `t`, money and date formatting for that locale.
 * Keys are `"<group>.<key>"`; each group lives in `./messages/<group>.ts`.
 * Outside a provider everything renders English, so a component reused on a
 * client screen keeps working unchanged.
 *
 * @module web/i18n
 */
import { createContext, useContext, useMemo, type ReactNode } from "react";
import { formatDate, formatDateTime, formatEur, formatNumber, interpolate, type Locale, type Vars } from "./core.ts";
import { common } from "./messages/common.ts";
import { dashboard } from "./messages/dashboard.ts";
import { billing } from "./messages/billing.ts";
import { availability } from "./messages/availability.ts";
import { onboarding } from "./messages/onboarding.ts";
import { handbook } from "./messages/handbook.ts";
import { hub } from "./messages/hub.ts";
import { fixedWeek } from "./messages/fixedWeek.ts";
import { cardDetails } from "./messages/cardDetails.ts";

export { localeFromLanguages, type Locale } from "./core.ts";

const GROUPS = { common, dashboard, billing, availability, onboarding, handbook, hub, cardDetails, fixedWeek };
type Groups = typeof GROUPS;

export type MessageKey = {
  [G in keyof Groups]: `${G & string}.${keyof Groups[G]["en"] & string}`;
}[keyof Groups];

function flatten(locale: Locale): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [g, pair] of Object.entries(GROUPS)) {
    for (const [k, v] of Object.entries(pair[locale] as Record<string, string>)) out[`${g}.${k}`] = v;
  }
  return out;
}

export const CATALOGS: Record<Locale, Record<string, string>> = { en: flatten("en"), de: flatten("de") };

/** Test-only marker wrapping every translated string, so a test can find text that bypassed `t`. */
export const PSEUDO_OPEN = "⟦";
export const PSEUDO_CLOSE = "⟧";

export interface I18n {
  locale: Locale;
  t: (key: MessageKey, vars?: Vars) => string;
  eur: (cents: number, opts?: { decimals?: "auto" | 2 }) => string;
  num: (n: number) => string;
  date: (iso: string | Date, opts: Intl.DateTimeFormatOptions) => string;
  dateTime: (iso: string | Date, opts: Intl.DateTimeFormatOptions) => string;
}

export function makeI18n(locale: Locale, pseudo = false): I18n {
  const wrap = pseudo ? (s: string) => `${PSEUDO_OPEN}${s}${PSEUDO_CLOSE}` : (s: string) => s;
  return {
    locale,
    // Missing German falls back to English (the type system and i18n.test.ts make that a build/test failure, never a user-visible gap).
    t: (key, vars) => wrap(interpolate(CATALOGS[locale][key] ?? CATALOGS.en[key] ?? key, vars)),
    eur: (cents, opts) => wrap(formatEur(cents, locale, opts)),
    num: (n) => wrap(formatNumber(n, locale)),
    date: (iso, opts) => wrap(formatDate(iso, locale, opts)),
    dateTime: (iso, opts) => wrap(formatDateTime(iso, locale, opts)),
  };
}

const I18nContext = createContext<I18n>(makeI18n("en"));

export function I18nProvider({ locale, pseudo = false, children }: { locale: Locale; pseudo?: boolean; children: ReactNode }) {
  const value = useMemo(() => makeI18n(locale, pseudo), [locale, pseudo]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

/** Test hook: forces pseudo mode for everything below, whatever locale the screen picks from `/api/editor/me`. */
const PseudoContext = createContext(false);
export function PseudoI18n({ children }: { children: ReactNode }) {
  return <PseudoContext.Provider value={true}>{children}</PseudoContext.Provider>;
}
export function usePseudo(): boolean {
  return useContext(PseudoContext);
}

export function useI18n(): I18n {
  return useContext(I18nContext);
}
